import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useAuth } from "../components/web3/context/AuthContext";
import { openPasswordModel } from "../components/social-wallet/PasswordModal";
import { ERC20_ABI } from "../components/web3/ERC20-utils/proxy-payment/HorusCoin_Constants";
// `signTransaction` lives as a non-exported const in utils.js; we access
// it through the existing `transferByEmail` / proxy-payment path below
// only when there's a good reason. For this hook we want a *direct*
// ERC-20 transfer (no proxy, no approval step) so the returned tx hash
// can be handed to the receipt page cleanly. signTransaction is the
// general-purpose custodial tx signer the backend exposes; we import
// it the same way ProxyPayment_Utils does.
import { signTransaction } from "../components/web3/utils/utils";

/**
 * State machine for the Horus Wallet (custodial) payment flow on
 * /pay/*. Sibling to `useX402Pay` and `useDirectPay` — same shape
 * so `CheckoutPanel` can read from any of the three without
 * branching on method-specific fields.
 *
 *   idle → password → signing → success
 *                  ↘ error
 *
 * Stages:
 *   - `password`  — PasswordModal open. If the wallet has no
 *                   password (`hasPassword === false`) we skip
 *                   straight to `signing` with an empty string.
 *   - `signing`   — backend `signTransaction` Lambda signs + broadcasts
 *                   a single `transfer(to, amount)` call on the token
 *                   contract. No proxy, no approval — a straight
 *                   ERC-20 transfer so the returned hash is the
 *                   settlement hash (not an approval's).
 *   - `success`   — receipt ready, CheckoutPanel navigates to
 *                   /pay/receipt/:txHash.
 *
 * Native tokens (`link.isNative === true`) are NOT supported in this
 * hook yet. The backend's signTransaction API expects a contract
 * call shape; plain value transfers go through the ProxyPayment
 * contract in the rest of the app, which costs more gas than a
 * simple transfer and batches recipients we don't need. CheckoutPanel
 * filters the horus method out for native links, so pay() is only
 * called on ERC-20 links in practice.
 */
export function useHorusPay(link) {
  const {
    isLoggedIn,
    loginOption,
    hasPassword,
    email,
    entityInfo,
  } = useAuth();

  const [phase, setPhase] = useState("idle");
  const [txHash, setTxHash] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);

  // Guard against double-taps on the Pay button popping two
  // password modals / two backend calls in a row.
  const inFlightRef = useRef(false);

  const reset = useCallback(() => {
    setPhase("idle");
    setTxHash(null);
    setReceipt(null);
    setError(null);
    inFlightRef.current = false;
  }, []);

  // Reset on link change so stale state from a previous link
  // doesn't bleed into the new page view — mirrors useDirectPay's
  // pattern.
  useEffect(() => {
    reset();
  }, [link?.linkId, reset]);

  // Prompt the password modal as a Promise so the pay() flow can
  // await it instead of juggling two callback branches. Resolves
  // with the password string on submit, `null` on cancel.
  //
  // PasswordModal calls `next(password)` on submit and `setLoading(false)`
  // on cancel — we wire both to the Promise resolver.
  const promptPassword = useCallback(() => {
    return new Promise((resolve) => {
      openPasswordModel(
        (password) => resolve(password || null),
        null,
        (flag) => {
          // setLoading(false) only fires on the cancel path; the
          // submit path closes without calling setLoading. So any
          // invocation here means "user dismissed the modal".
          if (flag === false) resolve(null);
        },
      );
    });
  }, []);

  const pay = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!isLoggedIn) {
      setError(new Error("Log in to pay with Horus Wallet."));
      setPhase("error");
      return;
    }
    if (loginOption !== "custodial") {
      setError(
        new Error(
          "Horus Wallet payment requires a custodial login (email / social / phone).",
        ),
      );
      setPhase("error");
      return;
    }
    if (!link) {
      setError(new Error("Payment link not loaded."));
      setPhase("error");
      return;
    }
    if (link.isNative) {
      // Guardrail — CheckoutPanel should have filtered us out
      // before the user could even pick this method.
      setError(
        new Error(
          "Native-token payments via Horus Wallet aren't supported yet. Use Direct instead.",
        ),
      );
      setPhase("error");
      return;
    }
    if (
      !link.tokenAddress ||
      link.tokenAddress === ethers.constants.AddressZero
    ) {
      setError(
        new Error("This link doesn't have a supported token for Horus Wallet."),
      );
      setPhase("error");
      return;
    }

    inFlightRef.current = true;
    setError(null);
    setReceipt(null);

    try {
      // 1. Password prompt — only when the wallet has one. Skipping
      //    the modal for no-password wallets matches
      //    tokens-exchange.js's existing behaviour (password == "" =>
      //    backend signs without verification).
      let password = "";
      if (hasPassword) {
        setPhase("password");
        const entered = await promptPassword();
        if (entered === null) {
          // User dismissed the modal — surface as a friendly error,
          // not a crash.
          const e = new Error("Password entry cancelled.");
          setError(e);
          setPhase("error");
          return;
        }
        password = entered;
      }

      // 2. Backend sign + broadcast. A single-element txs array with
      //    a plain ERC-20 transfer — no proxy, no approval, so the
      //    returned hash is the payment itself.
      setPhase("signing");
      const atomicAmount = ethers.utils
        .parseUnits(
          String(link.amountDisplay).replace(/[^\d.]/g, "") || "0",
          link.tokenDecimals,
        )
        .toString();

      const hash = await signTransaction(
        email,
        entityInfo?.indexWallet ?? 0,
        link.network, // e.g. "BASE" — existing Horus chain form
        password,
        [
          {
            contractAddress: link.tokenAddress,
            abi: ERC20_ABI,
            functionName: "transfer",
            params: [link.payTo, atomicAmount],
          },
        ],
        "Paying",
      );

      if (!hash) {
        throw new Error("Payment failed — no transaction hash returned.");
      }

      // 3. Build receipt in the shape ReceiptCard / ReceiptPage
      //    already accepts for the direct flow.
      setTxHash(hash);
      const receiptObj = {
        txHash: hash,
        method: "horus",
        timestamp: Date.now(),
        from: entityInfo?.activePublicKey || null,
        amountDisplay: link.amountDisplay,
      };
      setReceipt(receiptObj);
      setPhase("success");
    } catch (err) {
      const message = err?.message || "Horus Wallet payment failed.";
      const narrowed = new Error(message);
      narrowed.statusCode = err?.statusCode;
      narrowed.reason = err?.reason;
      setError(narrowed);
      setPhase("error");
    } finally {
      inFlightRef.current = false;
    }
  }, [
    isLoggedIn,
    loginOption,
    hasPassword,
    email,
    entityInfo,
    link,
    promptPassword,
  ]);

  return { phase, txHash, receipt, error, pay, reset };
}
